import { KatalComponent } from '../../shared/base';

/**
 * @component {kat-date-range-picker} KatalDaterangepicker Allows a user to select a range of dates and provides a display for the selected dates.
 * @guideline Do Best used when you want to collect a range of dates
 * @example UsLocale {"locale": "en-US"}
 * @example GermanLocale {"locale": "de-DE"}
 * @example DisabledWeekends {"id": "disabled-weekends", "locale": "en-US", "start-label": "Disabled Weekends",
 * "script": "
 *   var rangePicker = document.querySelector(\"kat-date-range-picker\");
 *   var disableWeekends = function (date) {
 *     return (date.getDay() === 0 || date.getDay() === 6);
 *   };
 *   rangePicker.startPicker.isDateDisabled = disableWeekends;
 *   rangePicker.endPicker.isDateDisabled = disableWeekends;
 * "}
 * @event change Change event will fire when either date is updated
 * @status Production
 * @theme flo
 */
class KatalDaterangepicker extends KatalComponent {
  static get observedAttributes() {
    return [
      'locale',
      'start-value',
      'end-value',
      'start-label',
      'end-label',
      'start-name',
      'end-name',
      'katal-1-2-spacing-fixes',
    ];
  }

  constructor() {
    super(KatalDaterangepicker.observedAttributes);
    this.initialLoad = true;
    this.startDatePicker = document.createElement('kat-date-picker');
    this.endDatePicker = document.createElement('kat-date-picker');
    this.rangeState = {
      startValue: '',
      endValue: '',
      startDate: '',
      endDate: '',
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
    this.initialLoad = false;
    this.createEventListeners();

    this.rangeState.startValue = this.startValue;
    this.rangeState.endValue = this.endValue;
  }

  createEventListeners() {
    this.startDatePicker.addEventListener('change', e => {
      e.stopImmediatePropagation();
      this.rangeState.startValue = e.detail.value;
      this.rangeState.startDate = e.detail.isoValue;
      const endDate = this.endDatePicker.dateValue;
      this.rangeState.endDate = endDate ? endDate.toISOString() : '';
      this.dispatchCustomEvent('change', { ...this.rangeState });

      this.setDateAttribute('start-value', e.detail.value);
    });

    this.endDatePicker.addEventListener('change', e => {
      e.stopImmediatePropagation();
      this.rangeState.endValue = e.detail.value;
      this.rangeState.endDate = e.detail.isoValue;
      const startDate = this.startDatePicker.dateValue;
      this.rangeState.startDate = startDate ? startDate.toISOString() : '';
      this.dispatchCustomEvent('change', { ...this.rangeState });

      this.setDateAttribute('end-value', e.detail.value);
    });
  }

  /**
   * @param {string} locale Set the locale for the date pickers
   * @required
   */
  get locale() {
    return this.getAttribute('locale') || 'en-US';
  }

  set locale(value) {
    this.setAttribute('locale', value);
  }

  /**
   * @param {string} start-label Set the label for the start date pickers
   * @required
   */
  get startLabel() {
    return this.getAttribute('start-label') || '';
  }

  set startLabel(value) {
    this.setAttribute('start-label', value);
  }

  /**
   * @param {string} end-label Set the label for the end date pickers
   * @required
   */
  get endLabel() {
    return this.getAttribute('end-label') || '';
  }

  set endLabel(value) {
    this.setAttribute('end-label', value);
  }

  /**
   * @classprop {string | Date} start-value Set the start date value for the date pickers
   */
  get startValue() {
    return this.getAttribute('start-value') || '';
  }

  set startValue(value) {
    this.rangeState.startValue = value;
    this.startDatePicker.value = value;

    this.setDateAttribute('start-value', value);
  }

  /**
   * @classprop {string | Date}  end-value Set the end date value for the date pickers
   */
  get endValue() {
    return this.getAttribute('end-value') || '';
  }

  set endValue(value) {
    this.rangeState.endValue = value;
    this.endDatePicker.value = value;

    this.setDateAttribute('end-value', value);
  }

  /**
   * @classprop {string} start-name The name of the start field, used when
   * sending form data to a server.
   */
  get startName() {
    return this.getAttributeOrDefault('start-name', '');
  }
  set startName(name) {
    this.setAttribute('start-name', name);
  }

  /**
   * @classprop {string} end-name The name of the end field, used when
   * sending form data to a server.
   */
  get endName() {
    return this.getAttributeOrDefault('end-name', '');
  }
  set endName(name) {
    this.setAttribute('end-name', name);
  }

  /**
   * @classprop {HTMLElement} start-picker The first date picker element that represents the start date.
   * @readonly
   */
  get startPicker() {
    return this.startDatePicker;
  }

  /**
   * @classprop {HTMLElement} end-picker The second date picker element that represents the end date.
   * @readonly
   */
  get endPicker() {
    return this.endDatePicker;
  }

  // Note: For the spacing attribute we just need the JSON metadata not the actual getter and setter.
  /**
   * @param {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
   * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
   * more details.
   */
  get katal12SpacingFixes() {
    return this.getBooleanAttribute('katal-1-2-spacing-fixes');
  }
  set katal12SpacingFixes(value) {
    return this.setBooleanAttribute('katal-1-2-spacing-fixes', value);
  }

  setDateAttribute(attrName, value) {
    if (value instanceof Date) {
      value = value.toISOString();
    }
    this.setAttribute(attrName, value);
  }

  render() {
    this.startDatePicker.locale = this.locale;
    this.startDatePicker.label = this.startLabel;
    this.startDatePicker.value = this.startValue;
    this.startDatePicker.name = this.startName;

    this.endDatePicker.locale = this.locale;
    this.endDatePicker.label = this.endLabel;
    this.endDatePicker.value = this.endValue;
    this.endDatePicker.name = this.endName;

    // Placed height fixing attribute on date pickers
    if (this.hasAttribute('katal-1-2-spacing-fixes')) {
      this.startDatePicker.setAttribute('katal-1-2-spacing-fixes', '');
      this.endDatePicker.setAttribute('katal-1-2-spacing-fixes', '');
    } else {
      this.startDatePicker.removeAttribute('katal-1-2-spacing-fixes');
      this.endDatePicker.removeAttribute('katal-1-2-spacing-fixes');
    }

    this.innerHTML = '';
    this.appendChild(this.startDatePicker);
    this.appendChild(this.endDatePicker);
  }

  // Respond to attribute changes.
  attributeChangedCallback() {
    if (!this.initialLoad) {
      this.render();
    }
  }
}

KatalComponent.register('kat-date-range-picker', KatalDaterangepicker);
